import { Contract, JsonRpcProvider, Wallet, MaxUint256, parseUnits, formatUnits } from 'ethers';

export const BSC_RPC = 'https://bsc-dataseed.binance.org/';

export const NPM_BY_DEX = {
  pancake: '0x46A15B0b27311cedF172AB29E4f4766fbE7F4364',
  uniswap: '0x7b8A01B39D58278b5DE7e48c8449c9f4F5170613',
};

const ERC20_ABI = [
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',
  'function balanceOf(address) view returns (uint256)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)',
];

const POOL_ABI = [
  'function token0() view returns (address)',
  'function token1() view returns (address)',
  'function fee() view returns (uint24)',
  'function tickSpacing() view returns (int24)',
  'function slot0() view returns (uint160 sqrtPriceX96, int24 tick, uint16 observationIndex, uint16 observationCardinality, uint16 observationCardinalityNext, uint32 feeProtocol, bool unlocked)',
];

const NPM_ABI = [
  'function factory() view returns (address)',
  'function mint((address token0,address token1,uint24 fee,int24 tickLower,int24 tickUpper,uint256 amount0Desired,uint256 amount1Desired,uint256 amount0Min,uint256 amount1Min,address recipient,uint256 deadline)) payable returns (uint256 tokenId, uint128 liquidity, uint256 amount0, uint256 amount1)',
];

function normalizePk(input) {
  let pk = String(input || '').trim();
  if (!pk) throw new Error('请填写私钥');
  if (!pk.startsWith('0x')) pk = `0x${pk}`;
  if (!/^0x[0-9a-fA-F]{64}$/.test(pk)) throw new Error('私钥格式不正确（需要 64 位十六进制）');
  return pk;
}

function dexKey(pair) {
  const id = String(pair?.dexBrand?.id || pair?.dexId || '').toLowerCase();
  if (id.includes('uni')) return 'uniswap';
  return 'pancake';
}

function feeUnits(pair) {
  const pct = Number(pair?.feeRatePct);
  if (Number.isFinite(pct) && pct > 0) return Math.round(pct * 10000);
  return 2500;
}

function nearestUsableTick(tick, spacing) {
  const spaced = Math.round(Number(tick) / Number(spacing)) * Number(spacing);
  // Uniswap tick bounds
  const MIN = -887272;
  const MAX = 887272;
  return Math.max(MIN, Math.min(MAX, spaced));
}

/**
 * 单边区间：
 * - 只放 token0：区间在现价上方（等待上涨，token0→token1）
 * - 只放 token1：区间在现价下方（等待下跌，token1→token0）
 */
function buildSingleSideTicks(currentTick, spacing, widthPct, depositIsToken0) {
  const width = Math.max(1, Number(widthPct) || 10);
  // 约每 1% ≈ 100 tick（粗略）；再按 spacing 对齐
  const rawSpan = Math.max(spacing * 2, Math.round((width / 100) * 10000));
  const span = Math.max(spacing * 2, Math.round(rawSpan / spacing) * spacing);
  const tick = Number(currentTick);

  if (depositIsToken0) {
    let tickLower = nearestUsableTick(tick + spacing, spacing);
    if (tickLower <= tick) tickLower += spacing;
    let tickUpper = tickLower + span;
    tickUpper = nearestUsableTick(tickUpper, spacing);
    if (tickUpper <= tickLower) tickUpper = tickLower + spacing;
    return { tickLower, tickUpper };
  }

  let tickUpper = nearestUsableTick(tick - spacing, spacing);
  if (tickUpper >= tick) tickUpper -= spacing;
  let tickLower = tickUpper - span;
  tickLower = nearestUsableTick(tickLower, spacing);
  if (tickLower >= tickUpper) tickLower = tickUpper - spacing;
  return { tickLower, tickUpper };
}

async function ensureAllowance(token, owner, spender, amount, signer) {
  const current = await token.allowance(owner, spender);
  if (current >= amount) return null;
  const tx = await token.connect(signer).approve(spender, MaxUint256);
  const receipt = await tx.wait();
  return receipt?.hash || tx.hash;
}

/**
 * 使用本地私钥在 BSC 上自动 Approve + Mint 单边 V3 仓位。
 * 私钥不会也不应上传到服务器。
 */
export async function autoMintSingleSideLp({
  privateKey,
  pair,
  side, // 'quote' | 'token'
  capitalUsd,
  widthPct = 10,
  slippageBps = 300, // 3%
  rpcUrl = BSC_RPC,
  onProgress,
}) {
  const log = (step, detail = '') => {
    try { onProgress?.(step, detail); } catch (_) {}
  };

  if (!pair?.pairAddress) throw new Error('缺少池子地址');
  if (!(Number(capitalUsd) > 0)) throw new Error('本金必须大于 0');

  const pk = normalizePk(privateKey);
  const provider = new JsonRpcProvider(rpcUrl, 56);
  const wallet = new Wallet(pk, provider);
  const npmAddress = NPM_BY_DEX[dexKey(pair)];
  if (!npmAddress) throw new Error('暂不支持该 DEX 的自动组池');

  log('连接钱包', wallet.address);

  const pool = new Contract(pair.pairAddress, POOL_ABI, provider);
  const npm = new Contract(npmAddress, NPM_ABI, wallet);

  const [token0Addr, token1Addr, feeOnchain, spacing, slot0] = await Promise.all([
    pool.token0(),
    pool.token1(),
    pool.fee(),
    pool.tickSpacing(),
    pool.slot0(),
  ]);

  const fee = Number(feeOnchain) || feeUnits(pair);
  const currentTick = Number(slot0.tick);
  const token0 = new Contract(token0Addr, ERC20_ABI, provider);
  const token1 = new Contract(token1Addr, ERC20_ABI, provider);

  const [dec0, dec1, sym0, sym1, bal0, bal1] = await Promise.all([
    token0.decimals(),
    token1.decimals(),
    token0.symbol().catch(() => 'T0'),
    token1.symbol().catch(() => 'T1'),
    token0.balanceOf(wallet.address),
    token1.balanceOf(wallet.address),
  ]);

  const tokenAddr = String(pair.tokenAddress || '').toLowerCase();
  const quoteAddr = String(pair.quoteAddress || '').toLowerCase();
  const t0 = String(token0Addr).toLowerCase();
  const t1 = String(token1Addr).toLowerCase();

  // 用户要单边投入的资产地址
  const depositAddr = (side === 'quote' ? quoteAddr : tokenAddr).toLowerCase();
  if (depositAddr !== t0 && depositAddr !== t1) {
    throw new Error('池子 token0/token1 与看板币对不匹配，已中止');
  }
  const depositIsToken0 = depositAddr === t0;

  // 单边区间方向必须与「只放哪一边」一致
  const { tickLower, tickUpper } = buildSingleSideTicks(currentTick, Number(spacing), widthPct, depositIsToken0);
  if (!(tickLower < tickUpper)) throw new Error('计算出的价格区间无效');

  const priceUsd = Number(pair.priceUsd);
  if (!(priceUsd > 0) && depositAddr === tokenAddr) {
    throw new Error('缺少目标币美元价格，无法换算投入数量');
  }

  // 本金换算成单边代币数量
  let amountHuman;
  if (depositAddr === quoteAddr) {
    // 报价币近似按 $1（USDT/USDC）；WBNB 则用 目标币价反推不稳，优先要求稳定币
    const quoteSym = String(pair.quoteSymbol || '').toUpperCase();
    if (['WBNB', 'BNB'].includes(quoteSym)) {
      throw new Error('自动单边暂不支持 WBNB 本金换算，请改选 /USDT 或 /USDC 池');
    }
    amountHuman = Number(capitalUsd);
  } else {
    amountHuman = Number(capitalUsd) / priceUsd;
  }
  if (!(amountHuman > 0)) throw new Error('换算后的投入数量无效');

  const depositToken = depositIsToken0 ? token0 : token1;
  const depositDecimals = depositIsToken0 ? Number(dec0) : Number(dec1);
  const amountDesired = parseUnits(amountHuman.toFixed(Math.min(8, depositDecimals)), depositDecimals);
  const balance = depositIsToken0 ? bal0 : bal1;
  if (balance < amountDesired) {
    const have = formatUnits(balance, depositDecimals);
    const need = formatUnits(amountDesired, depositDecimals);
    throw new Error(`余额不足：需要约 ${need}，钱包仅有 ${have}`);
  }

  const amount0Desired = depositIsToken0 ? amountDesired : 0n;
  const amount1Desired = depositIsToken0 ? 0n : amountDesired;
  // 单边 mint 时另一边为 0；min 也设 0，避免区间边界因价格微动失败（仍有本金与授权校验）
  const amount0Min = 0n;
  const amount1Min = 0n;
  const deadline = BigInt(Math.floor(Date.now() / 1000) + 20 * 60);

  log('读取池子', `${sym0}/${sym1} fee=${fee} tick=${currentTick} spacing=${spacing}`);
  log('单边区间', `tick ${tickLower} ~ ${tickUpper} · 投入 ${depositIsToken0 ? sym0 : sym1}`);

  const bnbBal = await provider.getBalance(wallet.address);
  if (bnbBal === 0n) throw new Error('钱包 BNB 为 0，无法支付 Gas');

  log('授权代币', npmAddress);
  const approveHash = await ensureAllowance(depositToken, wallet.address, npmAddress, amountDesired, wallet);
  if (approveHash) log('授权完成', approveHash);

  const mintParams = {
    token0: token0Addr,
    token1: token1Addr,
    fee,
    tickLower,
    tickUpper,
    amount0Desired,
    amount1Desired,
    amount0Min,
    amount1Min,
    recipient: wallet.address,
    deadline,
  };

  log('发送 mint', 'NonfungiblePositionManager.mint');
  const tx = await npm.mint(mintParams, { gasLimit: 900000n });
  log('等待确认', tx.hash);
  const receipt = await tx.wait();
  if (!receipt || receipt.status !== 1) throw new Error('组池交易失败或被回滚');

  return {
    ok: true,
    wallet: wallet.address,
    txHash: receipt.hash || tx.hash,
    npm: npmAddress,
    dex: dexKey(pair),
    fee,
    tickLower,
    tickUpper,
    currentTick,
    depositSymbol: depositIsToken0 ? sym0 : sym1,
    amountHuman,
    amount0Desired: amount0Desired.toString(),
    amount1Desired: amount1Desired.toString(),
    explorer: `https://bscscan.com/tx/${receipt.hash || tx.hash}`,
  };
}
